import { KEYCODE_TO_WEB_KEY_MAP } from "@eweadn-qiankun/shared-components/utils/keyCodeMapper";
import {
  BEIYING916_CB_VALUE_TO_LAYOUT_HID,
  KEYCODE_MAP_BEIYING,
  X98_1K_KEYS_BEIYING,
} from "@eweadn-qiankun/shared-components/utils/keyCodeMapperBeiying";
import {
  buildX75v2FactoryKeyboardKeysForGifLight,
  x75v2LightPosFromFactoryPos,
  X75V2_KNOB_DEVICE_VALUE,
} from "@eweadn-qiankun/shared-components/utils/x75v2FactoryPosFromInitJson";
import { BEIYING_8815_LAYER0_KEYBOARD_KEYS } from "@eweadn-qiankun/shared-components/utils/x98_1kBeiyingKeyboardRaw";

export type BeiyingGifLightKeyColor = {
  R: number;
  G: number;
  B: number;
  isCustom?: boolean;
};

export type BeiyingGifLightKeyColorMap = Record<
  string,
  BeiyingGifLightKeyColor
>;

export type BeiyingKeyboardKeyForGifLight = {
  value?: number;
  defaultValue?: number;
  keycode?: number;
  code?: number;
  light_pos?: number;
  lightPos?: number;
  pos?: number;
  name?: string;
};

/**
 * SDK @bytech/web-sdk-machine 1.4.50+：
 * Device8815.setGifLight 为 378 字节平面（126 槽 × RGB），slot i 对应 light_pos = 8 + i。
 */
export const BEIYING_8815_GIF_LIGHT_BASE_LIGHT_POS = 8;

export const BEIYING_8815_GIF_LIGHT_RGB_LENGTH = 126 * 3;

export const BEIYING_8815_GIF_LIGHT_SLOT_COUNT = 126;

const MECHA_FIRMWARE_TO_LAYOUT_HID: Readonly<Record<number, number>> = {
  65536: 224,
  131072: 225,
  262144: 226,
  524288: 227,
  1048576: 228,
  2097152: 229,
  4194304: 230,
  218103808: 61697,
};

const FACTORY_8815_GIF_LIGHT_KEYS: BeiyingKeyboardKeyForGifLight[] =
  BEIYING_8815_LAYER0_KEYBOARD_KEYS.filter((row) => {
    const lp = Number(row.light_pos);
    return (
      Number.isFinite(lp) &&
      lp >= BEIYING_8815_GIF_LIGHT_BASE_LIGHT_POS &&
      lp <
        BEIYING_8815_GIF_LIGHT_BASE_LIGHT_POS +
          BEIYING_8815_GIF_LIGHT_SLOT_COUNT
    );
  })
    .sort((a, b) => Number(a.light_pos) - Number(b.light_pos))
    .map((row) => ({
      value: row.value,
      light_pos: row.light_pos,
      pos: row.pos,
      name: row.name,
    }));

const FACTORY_8815_POS_TO_LIGHT_POS: ReadonlyMap<number, number> = (() => {
  const map = new Map<number, number>();
  for (const row of FACTORY_8815_GIF_LIGHT_KEYS) {
    map.set(Number(row.pos), Number(row.light_pos));
  }
  return map;
})();

/** 律动预览 KeyConfig.code 与 8815 setGifLight 查色 key 须同源 */
export function rhythmWebCodeFromLayoutHid(
  layoutHid: number
): string | undefined {
  if (!layoutHid) return undefined;
  const webKeySource = MECHA_FIRMWARE_TO_LAYOUT_HID[layoutHid] ?? layoutHid;
  return KEYCODE_TO_WEB_KEY_MAP[webKeySource] || `KEY_${layoutHid}`;
}

/** 8815 X98-1K：布局 pos → 工厂 light_pos → 预览 web code */
function build8815RhythmCodeByLightPos(): Map<number, string> {
  const map = new Map<number, string>();
  for (const slot of X98_1K_KEYS_BEIYING) {
    const lp = FACTORY_8815_POS_TO_LIGHT_POS.get(slot.pos);
    if (lp == null) continue;
    const code = rhythmWebCodeFromLayoutHid(slot.keycode);
    if (code) map.set(lp, code);
  }
  return map;
}

const RHYTHM_CODE_BY_LIGHT_POS_8815 = build8815RhythmCodeByLightPos();

/** slot 0..125 → 预览 web code（热路径避免 Map 查找） */
const RHYTHM_WEB_CODES_BY_SLOT_8815: Array<string | undefined> = (() => {
  const rows: Array<string | undefined> = new Array(
    BEIYING_8815_GIF_LIGHT_SLOT_COUNT
  );
  for (let slot = 0; slot < BEIYING_8815_GIF_LIGHT_SLOT_COUNT; slot += 1) {
    const lp = BEIYING_8815_GIF_LIGHT_BASE_LIGHT_POS + slot;
    rows[slot] = RHYTHM_CODE_BY_LIGHT_POS_8815.get(lp);
  }
  return rows;
})();

const REUSABLE_8815_RGB = new Array<number>(
  BEIYING_8815_GIF_LIGHT_RGB_LENGTH
).fill(0);

export function getBeiying8815FactoryKeyboardKeysForGifLight(): BeiyingKeyboardKeyForGifLight[] {
  return [...FACTORY_8815_GIF_LIGHT_KEYS];
}

export type BuildBeiying9168815GifLightRgbOptions = {
  /** X98-1K（8815）：走 378 字节静态平面 */
  is8815Device?: boolean;
  equipment?: Record<string, unknown> | null;
};

function pickRgb8815(
  color: BeiyingGifLightKeyColor | undefined
): [number, number, number] {
  if (!color) return [0, 0, 0];
  return [
    Math.max(0, Math.min(255, Math.round(color.R))),
    Math.max(0, Math.min(255, Math.round(color.G))),
    Math.max(0, Math.min(255, Math.round(color.B))),
  ];
}

function pickRgb916(
  color: BeiyingGifLightKeyColor | undefined
): [number, number, number] {
  if (!color || color.isCustom === false) return [0, 0, 0];
  return [
    Math.max(0, Math.min(255, Math.round(color.R))),
    Math.max(0, Math.min(255, Math.round(color.G))),
    Math.max(0, Math.min(255, Math.round(color.B))),
  ];
}

function lookupKeyColor(
  keyColors: BeiyingGifLightKeyColorMap,
  browserCode: string | undefined
): BeiyingGifLightKeyColor | undefined {
  if (!browserCode) return undefined;
  return keyColors[browserCode];
}

/** 8815 X98-1K：378 字节平面 RGB（写入 out，可复用缓冲区降 GC） */
function fill8815GifLightRgbPlane(
  keyColors: BeiyingGifLightKeyColorMap,
  out: number[]
): void {
  for (let slot = 0; slot < BEIYING_8815_GIF_LIGHT_SLOT_COUNT; slot += 1) {
    const browserCode = RHYTHM_WEB_CODES_BY_SLOT_8815[slot];
    const [r, g, b] = pickRgb8815(lookupKeyColor(keyColors, browserCode));
    const base = slot * 3;
    out[base] = r;
    out[base + 1] = g;
    out[base + 2] = b;
  }
}

function build8815GifLightRgbPlane(
  keyColors: BeiyingGifLightKeyColorMap
): number[] {
  fill8815GifLightRgbPlane(keyColors, REUSABLE_8815_RGB);
  return REUSABLE_8815_RGB;
}

function resolveLightPos(item: BeiyingKeyboardKeyForGifLight): number {
  const lp = item.light_pos ?? item.lightPos;
  if (typeof lp === "number" && Number.isFinite(lp)) return lp;
  if (typeof item.pos === "number" && Number.isFinite(item.pos))
    return item.pos;
  return Number.MAX_SAFE_INTEGER;
}

function resolveRawValue(
  item: BeiyingKeyboardKeyForGifLight
): number | undefined {
  /** 物理灯槽须按出厂 defaultValue；改键后的 value 会错位 */
  const raw = item.defaultValue ?? item.value ?? item.keycode ?? item.code;
  if (typeof raw !== "number" || !Number.isFinite(raw) || raw === 0) {
    return undefined;
  }
  return raw;
}

function isX75v2Equipment(equipment?: Record<string, unknown> | null): boolean {
  return (
    String(equipment?.keyboardType ?? "")
      .trim()
      .toLowerCase() === "x75v2"
  );
}

/**
 * 916 律动查色：与虚拟键盘 convertRowLayoutToKeyConfigs 同源
 *（MECHA 分位 → KEYCODE_TO_WEB_KEY_MAP → KEY_*），避免 KEYCODE_MAP_BEIYING.browserCode
 * 空串（Fn）或旋钮 AudioVolumeMute≠KEY_8418 导致实体与预览错位。
 */
function resolve916BrowserCode(
  item: BeiyingKeyboardKeyForGifLight
): string | undefined {
  const raw = resolveRawValue(item);
  if (raw === undefined) return undefined;

  let normalized = raw;
  if (normalized === X75V2_KNOB_DEVICE_VALUE) {
    normalized = 8418;
  }
  const uiHid = BEIYING916_CB_VALUE_TO_LAYOUT_HID[normalized];
  if (typeof uiHid === "number" && Number.isFinite(uiHid)) {
    normalized = uiHid;
  }

  const fromLayout = rhythmWebCodeFromLayoutHid(normalized);
  if (fromLayout) return fromLayout;

  const cap = KEYCODE_MAP_BEIYING.find(
    (k) => k.keycode === normalized || k.xsKeycode === normalized
  );
  return cap?.browserCode || undefined;
}

/**
 * 916（M71/M80）：将律动键色映射为 setGifLight 平面 RGB。
 * 键序按 getKeyboardKeys 返回项的 light_pos 升序（8 为第一键，9 为第二键，…）。
 */
function build916GifLightRgbFromKeyboardKeys(
  keyColors: BeiyingGifLightKeyColorMap,
  keyboardKeys: BeiyingKeyboardKeyForGifLight[]
): number[] {
  const sorted = [...(keyboardKeys ?? [])].sort(
    (a, b) => resolveLightPos(a) - resolveLightPos(b)
  );
  const out: number[] = [];
  for (const item of sorted) {
    const code = resolve916BrowserCode(item);
    const [r, g, b] = pickRgb916(code ? keyColors[code] : undefined);
    out.push(r, g, b);
  }
  return out;
}

const REUSABLE_X75V2_RGB = new Array<number>(
  BEIYING_8815_GIF_LIGHT_RGB_LENGTH
).fill(0);

function resolveX75v2SlotLightPos(
  item: BeiyingKeyboardKeyForGifLight
): number | undefined {
  const lp = item.light_pos ?? item.lightPos;
  if (typeof lp === "number" && Number.isFinite(lp)) return lp;
  if (typeof item.pos === "number" && Number.isFinite(item.pos)) {
    return x75v2LightPosFromFactoryPos(item.pos);
  }
  return undefined;
}

/**
 * X75V2：Device916.setGifLight 与 8815 一样吃 378 字节稠密平面
 *（slot i ↔ light_pos=8+i）。INIT/在线表 pos 有空洞，紧凑拼接会整体错位。
 */
function buildX75v2DenseGifLightRgbPlane(
  keyColors: BeiyingGifLightKeyColorMap,
  keyboardKeys?: BeiyingKeyboardKeyForGifLight[] | null
): number[] {
  const out = REUSABLE_X75V2_RGB;
  out.fill(0);

  const source =
    keyboardKeys && keyboardKeys.length > 0
      ? keyboardKeys
      : buildX75v2FactoryKeyboardKeysForGifLight();

  for (const item of source) {
    const lp = resolveX75v2SlotLightPos(item);
    if (lp == null || !Number.isFinite(lp)) continue;
    const slot = lp - BEIYING_8815_GIF_LIGHT_BASE_LIGHT_POS;
    if (slot < 0 || slot >= BEIYING_8815_GIF_LIGHT_SLOT_COUNT) continue;
    const code = resolve916BrowserCode(item);
    const [r, g, b] = pickRgb916(code ? keyColors[code] : undefined);
    const base = slot * 3;
    out[base] = r;
    out[base + 1] = g;
    out[base + 2] = b;
  }
  return out;
}

/**
 * 贝盈 916 / 8815 律动 RGB：
 * - 8815 X98-1K：378 字节静态平面
 * - X75V2：378 字节稠密平面（light_pos 按 pos 公式，填空洞）
 * - 916 M71/M80 等：getKeyboardKeys + light_pos 排序
 */
export function buildBeiying9168815GifLightRgb(
  keyColors: BeiyingGifLightKeyColorMap,
  keyboardKeys?: BeiyingKeyboardKeyForGifLight[] | null,
  options?: BuildBeiying9168815GifLightRgbOptions
): number[] {
  if (options?.is8815Device) {
    return build8815GifLightRgbPlane(keyColors);
  }
  if (isX75v2Equipment(options?.equipment)) {
    /** 始终出厂表：改键后 value 变化不影响物理灯位；light_pos 按 pos 公式填稠密平面 */
    return buildX75v2DenseGifLightRgbPlane(keyColors, null);
  }
  return build916GifLightRgbFromKeyboardKeys(keyColors, keyboardKeys ?? []);
}

/** X75V2 出厂键表（供 getKeyboardKeys 失败时兜底） */
export function getBeiyingX75v2FactoryKeyboardKeysForGifLight(): BeiyingKeyboardKeyForGifLight[] {
  return buildX75v2FactoryKeyboardKeysForGifLight();
}
